import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {isOk} from '../../../utils/apiResponse';
import {getTranslatedName, looksLikeOutcome, overlapsWithSelected, stripActionPrefix} from '../../selects/actionCategories';
import {formatCompactAmount} from './panelFormatters';

// Cuántos eventos se proponen. Con más, el bloque deja de ser una ayuda y se vuelve una lista, que
// es justamente lo que el usuario no pudo resolver en el modal grande.
const MAX_SUGGESTIONS = 3;

/**
 * De los eventos con datos, los N primeros que NO se solapan entre sí.
 *
 * Meta publica el mismo hecho con varias agrupaciones: «Contenido visto agrupado» y «Contenido
 * visto del píxel» son el mismo número, y el primero incluye al segundo. Sin esta poda, dos de
 * los tres lugares de la sugerencia decían lo mismo — y el propio producto, en la pantalla de al
 * lado, avisa en ámbar que elegir los dos cuenta doble.
 *
 * Se conserva el de más volumen de cada familia (la lista llega ordenada), que es el criterio
 * menos arbitrario disponible sin saber cuál es la conversión del negocio.
 */
export function pickDistinctSuggestions(actionsWithData, max = MAX_SUGGESTIONS) {
    // Primero los que parecen un RESULTADO (compras, leads, citas, suscripciones, conversiones
    // propias) y recién después los de actividad, cada grupo por volumen. Ordenar sólo por volumen
    // ponía arriba siempre lo mismo —«Interacción con la página», «Reproducciones de video»—, que
    // son los eventos con más volumen de cualquier cuenta y casi nunca son la conversión: medido en
    // una cuenta real, de los 3 propuestos 2 eran engagement y el único de resultado quedaba afuera.
    const ordenado = [...actionsWithData].sort((a, b) => {
        const ra = looksLikeOutcome(a.action_id, {isCustom: a.is_custom}) ? 0 : 1;
        const rb = looksLikeOutcome(b.action_id, {isCustom: b.is_custom}) ? 0 : 1;
        if (ra !== rb) return ra - rb;
        return (b.counts.last_30_days || 0) - (a.counts.last_30_days || 0);
    });

    const kept = [];
    for (const action of ordenado) {
        if (kept.length >= max) break;
        const keptIds = kept.map(each => each.action_id);
        if (overlapsWithSelected(action.action_id, keptIds).length > 0) continue;
        kept.push(action);
    }
    return kept;
}

/**
 * Qué hacer cuando una cuenta del panel dice "Sin configurar".
 *
 * El panel ya decía QUE faltaba y ahí se terminaba: para saber qué elegir había que abrir el modal
 * de eventos y decidir entre más de cien renglones, sin saber cuáles registra esa cuenta. Este
 * bloque trae los que registró —con su número— y los deja elegir de a uno, en el lugar.
 *
 * Lo que NO hace, a propósito: elegir solo. Un evento con volumen no es necesariamente la
 * conversión del negocio, y una línea midiendo la conversión equivocada da números lindos y no se
 * queja nunca. Por eso el rótulo dice qué registra la cuenta, nunca "te recomendamos".
 */
export function UnconfiguredAccountSuggestion({account, onSaved, onOpenAll}) {
    const {t} = useTranslation();
    // `suggestions` son los chips que se dibujan (podados y truncados); `total` es cuántos eventos
    // registró la cuenta, que es lo que dice el rótulo. Son dos números distintos: mezclarlos hacía
    // que el rótulo dijera «registró 3 eventos» cuando registró 32 y sólo se ofrecen 3.
    const [state, setState] = useState({status: 'LOADING', suggestions: [], total: 0});
    const [saving, setSaving] = useState(null);
    // Una vez elegido, el bloque no vuelve a aceptar clicks. El endpoint NO es aditivo —reemplaza
    // la lista entera—, así que un segundo click en la ventana que va desde que termina el POST
    // hasta que el panel se refresca borraba el primer evento, en silencio y sin que nada en la
    // pantalla dijera que la elección era excluyente.
    const [picked, setPicked] = useState(null);

    useEffect(() => {
        if (!account.ad_account_id) return undefined;

        let cancelled = false;
        const url = `${API_URL}agency/ad-accounts/${account.ad_account_id}/actions`;

        // `cache: true` porque este GET puede salir a la API de la plataforma (el comando
        // sincroniza las conversion actions cuando la cuenta no tiene ninguna guardada): sin el
        // flag, un cliente con 3 cuentas sin configurar dispara 3 llamadas por cada apertura.
        new APIClient(url).call('GET', {}, {cache: true}).then((response) => {
            if (cancelled) return;
            if (!isOk(response) || !Array.isArray(response.data)) {
                // Silencio: el modal ya cumple su función sin esta ayuda, y un error acá sobre el
                // estado "sin configurar" sería ruido encima de un problema que el usuario ya ve.
                setState({status: 'ERROR', suggestions: [], total: 0});
                return;
            }

            const withData = response.data
                .filter(action => action.counts && (action.counts.yesterday
                    || action.counts.last_7_days || action.counts.last_30_days))
                .sort((a, b) => (b.counts.last_30_days || 0) - (a.counts.last_30_days || 0));

            setState({
                status: 'READY',
                suggestions: pickDistinctSuggestions(withData),
                total: withData.length,
            });
        }).catch(() => {
            if (!cancelled) setState({status: 'ERROR', suggestions: [], total: 0});
        });

        return () => { cancelled = true; };
    }, [account.ad_account_id]);

    const handlePick = useCallback((actionId) => {
        setSaving(actionId);
        const url = `${API_URL}client/ad_account/${account.id}/selected_actions`;
        new APIClient(url).call('POST', {selected_actions: [actionId]}).then((response) => {
            setSaving(null);
            // Sin el `isOk`, un 4xx/5xx o un NETWORK_ERROR dispararía el `onSaved` del modal, que
            // muestra "Conversión actualizada" sobre algo que no se guardó.
            if (!isOk(response)) {
                toast.error(t('client_panel.conversion_suggest_error'));
                return;
            }
            setPicked(actionId);
            if (onSaved) onSaved();
        }).catch(() => {
            setSaving(null);
            toast.error(t('client_panel.conversion_suggest_error'));
        });
    }, [account.id, onSaved, t]);

    if (state.status !== 'READY' || state.suggestions.length === 0) return null;

    const labelOf = (action) => getTranslatedName(action.action_id)
        || action.description
        || stripActionPrefix(action.action_id);

    return (
        <div className="pnl-conv__suggest">
            <p className="pnl-conv__suggest-label">
                {t('client_panel.conversion_suggest_label', {count: state.total})}
            </p>
            {/* Decir que el click GUARDA. Una fila de pastillas bajo un rótulo descriptivo se lee
                como un resumen, no como una escritura inmediata. */}
            <p className="pnl-conv__suggest-hint">{t('client_panel.conversion_suggest_hint')}</p>
            <div className="pnl-conv__suggest-chips">
                {state.suggestions.map(action => (
                    <button
                        key={action.action_id}
                        type="button"
                        className={`pnl-conv__suggest-chip ${picked === action.action_id ? '-picked' : ''}`}
                        disabled={saving !== null || picked !== null}
                        onClick={() => handlePick(action.action_id)}
                    >
                        <span className="chip-name">{labelOf(action)}</span>
                        <span className="chip-count">
                            {formatCompactAmount(action.counts.last_30_days)}
                        </span>
                    </button>
                ))}
                {/* Sin esta salida, tres pastillas debajo de "registró 23 eventos" se leen como si
                    fueran las tres únicas opciones. El botón abre la lista completa, que es el
                    mismo modal del "Editar eventos" de la fila. */}
                {onOpenAll && (
                    <button type="button" className="pnl-conv__suggest-all"
                            disabled={saving !== null || picked !== null}
                            onClick={onOpenAll}>
                        {t('client_panel.conversion_suggest_see_all', {count: state.total})}
                    </button>
                )}
            </div>
        </div>
    );
}

export default UnconfiguredAccountSuggestion;
